import { computed, onMounted, ref } from 'vue';
import { supportApi, type ContactChannels } from '@/api/support';
import { isEmail, isTelegram, isWhatsapp } from './contactChannelRules';
import { take } from '@/api/warmCache';
import { CONTACT_CHANNELS_KEY } from '@/components/settingsProbes';
import { getActivePinia } from 'pinia';
import { useAuthStore } from '@/stores/auth';

export type ContactKind = 'telegram' | 'whatsapp' | 'email';

export interface ContactLink {
  kind: ContactKind;
  href: string;
}

/**
 * The manager's other channels (DVL-010). The links come only from the backoffice — nothing is hardcoded — and a
 * channel nobody configured is simply absent, so on a fresh install the client sees the in-app chat alone.
 */
export function useContactChannels() {
  // Outside an app (isolated component tests) there is no store: no client, so no warm cache to read from.
  const auth = getActivePinia() ? useAuthStore() : null;
  const channels = ref<ContactChannels | null>(null);
  /** True once the channels are read (or failed) — a screen can draw its rows all at once. */
  const ready = ref(false);

  const links = computed<ContactLink[]>(() => {
    const c = channels.value;
    if (!c) return [];
    const out: ContactLink[] = [];
    // The server validates on save; the app checks again, by the same rules, before a value becomes a tap target
    // (defence in depth) — so a value saved in the backoffice is never silently missing here.
    if (c.telegramUrl && isTelegram(c.telegramUrl)) out.push({ kind: 'telegram', href: c.telegramUrl });
    if (c.whatsappUrl && isWhatsapp(c.whatsappUrl)) out.push({ kind: 'whatsapp', href: c.whatsappUrl });
    if (c.email && isEmail(c.email)) out.push({ kind: 'email', href: `mailto:${c.email}` });
    return out;
  });

  onMounted(async () => {
    try {
      channels.value = await take(auth?.customerId ?? null, CONTACT_CHANNELS_KEY, () => supportApi.contactChannels());
    } catch {
      channels.value = null;   // no extra channels shown — the chat itself still works
    } finally {
      ready.value = true;
    }
  });

  return { links, ready };
}
